/**
 * Offline Context Provider
 * Provides network status and sync functionality throughout the app
 *
 * Features:
 * - Real-time online/offline detection
 * - Pending sync count tracking
 * - Manual and automatic sync triggers
 * - Sync status notifications
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  ReactNode
} from 'react';
import { offlineDb } from '../lib/offline/db';
import { OFFLINE_CONFIG } from '../lib/offline/constants';

// ============== TYPES ==============

export type SyncState = 'idle' | 'syncing' | 'success' | 'error';

export interface OfflineContextType {
  // Network status
  isOnline: boolean;

  // Sync status
  pendingSyncCount: number;
  failedSyncCount: number;
  lastSyncAt: Date | null;
  syncState: SyncState;
  syncError: string | null;

  // Actions
  triggerSync: () => Promise<void>;
  refreshPendingCount: () => Promise<void>;

  // Helpers
  isSyncing: boolean;
  hasPendingChanges: boolean;
}

// ============== CONTEXT ==============

const OfflineContext = createContext<OfflineContextType | undefined>(undefined);

// ============== PROVIDER ==============

interface OfflineProviderProps {
  children: ReactNode;
}

export function OfflineProvider({ children }: OfflineProviderProps) {
  // Network status
  const [isOnline, setIsOnline] = useState<boolean>(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );

  // Sync status
  const [pendingSyncCount, setPendingSyncCount] = useState<number>(0);
  const [failedSyncCount, setFailedSyncCount] = useState<number>(0);
  const [lastSyncAt, setLastSyncAt] = useState<Date | null>(null);
  const [syncState, setSyncState] = useState<SyncState>('idle');
  const [syncError, setSyncError] = useState<string | null>(null);

  // Refs for cleanup and latest values
  const syncTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef<boolean>(true);
  const triggerSyncRef = useRef<() => Promise<void>>(() => Promise.resolve());

  // ============== NETWORK STATUS DETECTION ==============

  useEffect(() => {
    const handleOnline = () => {
      console.log('[OfflineContext] Network status: ONLINE');
      setIsOnline(true);

      // Auto-sync after delay when coming online
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
      syncTimeoutRef.current = setTimeout(() => {
        if (isMountedRef.current) {
          // Use ref to get latest triggerSync function
          triggerSyncRef.current();
        }
      }, OFFLINE_CONFIG.AUTO_SYNC_DELAY_MS);
    };

    const handleOffline = () => {
      console.log('[OfflineContext] Network status: OFFLINE');
      setIsOnline(false);

      // Clear any pending sync timeout
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
        syncTimeoutRef.current = null;
      }
    };

    // Add event listeners
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Initial status
    setIsOnline(navigator.onLine);

    // Cleanup
    return () => {
      isMountedRef.current = false;
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
    };
  }, []);

  // ============== PENDING COUNT TRACKING ==============

  const refreshPendingCount = useCallback(async () => {
    try {
      const [pending, failed] = await Promise.all([
        offlineDb.getPendingCount(),
        offlineDb.getFailedCount(),
      ]);

      if (isMountedRef.current) {
        setPendingSyncCount(pending);
        setFailedSyncCount(failed);
      }
    } catch (error) {
      console.error('[OfflineContext] Error refreshing pending count:', error);
    }
  }, []);

  // Refresh pending count on mount and periodically
  useEffect(() => {
    refreshPendingCount();

    // Refresh every 30 seconds
    const interval = setInterval(refreshPendingCount, 30000);

    return () => clearInterval(interval);
  }, [refreshPendingCount]);

  // ============== AUTO-SYNC ON MOUNT ==============
  // Trigger sync on mount if there are pending operations and backend is reachable
  // This handles the case where browser was always "online" but backend was down
  useEffect(() => {
    const checkAndSyncOnMount = async () => {
      // Wait a bit for counts to be refreshed
      await new Promise(resolve => setTimeout(resolve, 1000));

      if (!isMountedRef.current || !navigator.onLine) return;

      try {
        // Check if there are pending operations
        const pendingCount = await offlineDb.getPendingCount();

        if (pendingCount > 0) {
          console.log(`[OfflineContext] Found ${pendingCount} pending operations on mount - checking backend availability...`);

          // Try to reach the backend with a simple health check
          const token = localStorage.getItem('saubhagya_jwt_token');
          const isDev = import.meta.env.DEV;
          const baseUrl = isDev ? '/gaushala-service' : (import.meta.env.VITE_GAUSHALA_SERVICE_URL || 'http://localhost:8086/gaushala-service');

          try {
            const response = await fetch(`${baseUrl}/api/v1/gaushala/master/genders`, {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                ...(token && { Authorization: `Bearer ${token}` }),
              },
            });

            if (response.ok) {
              console.log('[OfflineContext] Backend is reachable - triggering auto-sync...');
              // Small delay to ensure everything is initialized
              setTimeout(() => {
                if (isMountedRef.current) {
                  triggerSyncRef.current();
                }
              }, 500);
            } else {
              console.log('[OfflineContext] Backend returned error - sync deferred');
            }
          } catch (fetchError) {
            console.log('[OfflineContext] Backend not reachable - sync deferred');
          }
        }
      } catch (error) {
        console.error('[OfflineContext] Error checking pending operations on mount:', error);
      }
    };

    checkAndSyncOnMount();
  }, []); // Run once on mount

  // ============== SYNC TRIGGER ==============

  const triggerSync = useCallback(async () => {
    // Don't sync if offline or already syncing
    if (!isOnline || syncState === 'syncing') {
      console.log('[OfflineContext] Sync skipped:', {
        isOnline,
        syncState,
      });
      return;
    }

    console.log('[OfflineContext] Starting sync...');
    setSyncState('syncing');
    setSyncError(null);

    try {
      // Get pending operations
      const pendingOps = await offlineDb.pendingOperations
        .where('status')
        .equals('pending')
        .toArray();

      if (pendingOps.length === 0) {
        console.log('[OfflineContext] No pending operations to sync');
        setSyncState('success');
        setLastSyncAt(new Date());
        return;
      }

      console.log(`[OfflineContext] Processing ${pendingOps.length} pending operations`);

      // Process operations in batches
      let successCount = 0;
      let failCount = 0;

      for (const op of pendingOps) {
        try {
          // Import sync processor dynamically to avoid circular deps
          const { processSyncOperation } = await import('../lib/offline/syncQueue');
          const success = await processSyncOperation(op);

          if (success) {
            successCount++;
          } else {
            failCount++;
          }
        } catch (error) {
          console.error(`[OfflineContext] Error processing operation ${op.id}:`, error);
          failCount++;
        }
      }

      console.log(`[OfflineContext] Sync complete: ${successCount} success, ${failCount} failed`);

      // Update counts
      await refreshPendingCount();

      if (isMountedRef.current) {
        setLastSyncAt(new Date());
        setSyncState(failCount > 0 ? 'error' : 'success');
        if (failCount > 0) {
          setSyncError(`${failCount} operation(s) failed to sync`);
        }
      }
    } catch (error) {
      console.error('[OfflineContext] Sync error:', error);
      if (isMountedRef.current) {
        setSyncState('error');
        setSyncError(error instanceof Error ? error.message : 'Sync failed');
      }
    }
  }, [isOnline, syncState, refreshPendingCount]);

  // Keep ref updated with latest triggerSync
  useEffect(() => {
    triggerSyncRef.current = triggerSync;
  }, [triggerSync]);

  // ============== CONTEXT VALUE ==============

  const value: OfflineContextType = {
    // Network status
    isOnline,

    // Sync status
    pendingSyncCount,
    failedSyncCount,
    lastSyncAt,
    syncState,
    syncError,

    // Actions
    triggerSync,
    refreshPendingCount,

    // Helpers
    isSyncing: syncState === 'syncing',
    hasPendingChanges: pendingSyncCount > 0 || failedSyncCount > 0,
  };

  return (
    <OfflineContext.Provider value={value}>
      {children}
    </OfflineContext.Provider>
  );
}

// ============== HOOK ==============

export function useOffline(): OfflineContextType {
  const context = useContext(OfflineContext);
  if (context === undefined) {
    throw new Error('useOffline must be used within an OfflineProvider');
  }
  return context;
}

// ============== SIMPLE NETWORK STATUS HOOK ==============

/**
 * Simple hook for just network status (doesn't require provider)
 */
export function useNetworkStatus(): { isOnline: boolean } {
  const [isOnline, setIsOnline] = useState<boolean>(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return { isOnline };
}
